import { useRef, useState } from "react"
import styles from "./DateRangePicker.module.scss"

import { useTranslation } from "react-i18next"
import { DaySelector } from "./DaySelector"
import { ContentBubble, Placement } from "../ContentBubble/ContentBubble"

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
}

interface Props {
  /** Start date of selected range */
  start: Date | undefined
  /** End date of selected range */
  end: Date | undefined
  /** Minimum selectable date */
  min?: Date
  /** Maximum selectable date */
  max?: Date
  /** Clear selected date buttons are available when `true` */
  clearable?: boolean
  /** Set horizontal alignment of date fields */
  horizontal?: boolean
  /** Format date display as UTC, date picker still uses local time and returned dates need to be processed */
  displayAsUTC?: boolean
  /** Callback that returns user's selected date range
   * @param start Start date
   * @param end End date
   */
  onRangeSelect: (start: Date | undefined, end: Date | undefined) => void
}

enum SelectorState{
  START,
  END,
}

export function DateRangePicker({
  start,
  end,
  min,
  max,
  clearable,
  horizontal,
  displayAsUTC,
  onRangeSelect,
}: Props) {
  const { t } = useTranslation()

  const [show, setShow] = useState(false)
  const [selectorState, setSelectorState] = useState(SelectorState.START)
  const [selectedStart, setSelectedStart] = useState<Date>()
  const [selectedEnd, setSelectedEnd] = useState<Date>()
  const target = useRef<HTMLDivElement>(null)
  const targetStart = useRef<HTMLDivElement>(null)
  const targetEnd = useRef<HTMLDivElement>(null)

  function openModal() {
    setSelectedStart(start && new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    ))
    setSelectedEnd(end && new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
    ))
    setShow(true)
  }

  let date
  if (selectorState === SelectorState.START) date = selectedStart ?? selectedEnd
  if (selectorState === SelectorState.END) date = selectedEnd ?? selectedStart
  if (!date) date = new Date()

  return <div
    className={styles.component}
    ref={target}
    style={{flexDirection: horizontal ? "row" : "column"}}
  >
    <div className={styles["date-range-block"]}>
      <p>{t("Start Date")}</p>
      <p
        ref={targetStart}
        onClick={() => {
          setSelectorState(SelectorState.START)
          openModal()
        }}
        style={{
          outline: (selectorState === SelectorState.START && show) ? "1px solid #454545" : ""
        }}
      >
        <i className="fa fa-calendar" />
        <span>{start
          ? start.toLocaleDateString(
              "en-GB",
              {...DATE_FORMAT, ...(displayAsUTC ? {timeZone: "UTC"} : {})},
            )
          : ""
        }</span>
        { clearable && start &&
          <i
            className="fa fa-xmark"
            onClick={e => {
              e.stopPropagation()
              onRangeSelect(undefined, undefined)
            }}
          />
        }
      </p>
    </div>
    <div className={styles["date-range-block"]}>
      <p>{t("End Date")}</p>
      <p
        ref={targetEnd}
        onClick={() => {
          setSelectorState(SelectorState.END)
          openModal()
        }}
        style={{
          outline: (selectorState === SelectorState.END && show) ? "1px solid #454545" : ""
        }}
      >
        <i className="fa fa-calendar" />
        <span>{end
          ? end.toLocaleDateString(
              "en-GB",
              {...DATE_FORMAT, ...(displayAsUTC ? {timeZone: "UTC"} : {})},
            )
          : ""
        }</span>
        { clearable && end &&
          <i
            className="fa fa-xmark"
            onClick={e => {
              e.stopPropagation()
              onRangeSelect(undefined, undefined)
            }}
          />
        }
      </p>
    </div>
    <ContentBubble
      target={target}
      show={show}
      arrowTarget={selectorState === SelectorState.START ? targetStart : targetEnd}
      preferredPlacements={horizontal
        ? [Placement.TOP, Placement.BOTTOM]
        : [Placement.RIGHT, Placement.LEFT]
      }
      close={() => setShow(false)}
    >
      <div className={styles.bubble}>
        <DaySelector
          date={date}
          min={min}
          max={max}
          onSelect={day => {
            if (selectorState === SelectorState.START) {
              setSelectedStart(day)
              if (selectedEnd) {
                setShow(false)
                onRangeSelect(day, new Date(selectedEnd.getFullYear(), selectedEnd.getMonth(), selectedEnd.getDate()+1, 0, 0, 0, -1))
              }
              else {
                setSelectorState(SelectorState.END)
              }
            }
            if (selectorState === SelectorState.END) {
              setSelectedEnd(day)
              if (selectedStart) {
                setShow(false)
                onRangeSelect(selectedStart, new Date(day.getFullYear(), day.getMonth(), day.getDate()+1, 0, 0, 0, -1))
              }
              else {
                setSelectorState(SelectorState.START)
              }
            }
          }}
          daySelectable={day => {
            if (selectorState === SelectorState.START) {
              if (!selectedEnd) return true
              if (day > selectedEnd) return false
              return true
            }
            else {
              if (!selectedStart) return true
              if (day < selectedStart) return false
              return true
            }
          }}
          daySelected={day => {
            if (!selectedStart || !selectedEnd) return false
            return day >= selectedStart && day <= selectedEnd
          }}
          dayStyle={day =>
            day.getTime() === selectedStart?.getTime() || day.getTime() === selectedEnd?.getTime()
            ? {backgroundColor: "#BCCF00"}
            : undefined
          }
        />
      </div>
    </ContentBubble>
  </div>
}